// Discover step container. Hosts the Scan | Manual mode toggle and the active body (StepScan or
// ManualConditionPicker). Both bodies render their OWN two-column split (list/picker on the left,
// a sticky chart/results inspector on the right) and share one backtest state (useDraftBacktest),
// so a report opened in either mode is the same assembled-strategy report.
//
// Both bodies write the assembled strategy into the same draft (long/short/exit cond indices) and
// call bt.reset() to invalidate a stale report; both trigger the same Backtest run via bt.run().

import { useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDraftBacktest } from "../../../hooks/useDraftBacktest";
import { StepScan } from "./StepScan";
import { ManualConditionPicker } from "./ManualConditionPicker";

type Mode = "scan" | "manual";

interface Props {
  /** Advance to the Activate step. */
  onAdvance: () => void;
}

export function StepDiscover({ onAdvance }: Props) {
  const [mode, setMode] = useState<Mode>("scan");
  const bt = useDraftBacktest();

  return (
    <div className="flex flex-col gap-5">
      {/* Mode toggle */}
      <div className="flex items-center gap-1.5 p-1 rounded-2xl border border-border bg-card-elevated w-fit">
        <ModeButton active={mode === "scan"} onClick={() => setMode("scan")} icon={<Search className="size-4" />}>
          Scan (auto)
        </ModeButton>
        <ModeButton active={mode === "manual"} onClick={() => setMode("manual")} icon={<SlidersHorizontal className="size-4" />}>
          Manual
        </ModeButton>
      </div>

      {mode === "scan" ? (
        <StepScan bt={bt} onAdvance={onAdvance} />
      ) : (
        <ManualConditionPicker bt={bt} onAdvance={onAdvance} />
      )}
    </div>
  );
}

function ModeButton({
  active, onClick, icon, children,
}: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-4 h-9 rounded-xl text-sm font-semibold transition-all cursor-pointer",
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/40",
      )}
    >
      {icon}
      {children}
    </button>
  );
}
